import React, { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { Grid, Stack } from '@mui/material';
import { isEmpty } from 'lodash';

import {
  GET_PAGINATED_PROVIDERS,
  GET_PROVIDERS_FAVORITE,
  GET_PROVIDERS_MOST_RECENT,
} from './gql/Query';
import clientGraphql from '../../common/apollo-graphql';
import FindClinicTable from './tables/FindClinicTable';
import FindClinicFaveRecentTable from './tables/FileClinicFaveRecentTable';

export default function FindClinic({ handleOnClickClinic }) {
  const [keyword, setKeyword] = useState('');
  const [parsedData, setParsedData] = useState({});
  const [parsedFavorites, setParsedFavorites] = useState({});
  const [parsedMostRecent, setParsedMostRecent] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { loading, data, refetch } = useQuery(GET_PAGINATED_PROVIDERS, {
    variables: {
      keyword,
      page,
      limit: pageSize,
    },
    client: clientGraphql,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const { data: favorites } = useQuery(GET_PROVIDERS_FAVORITE, {
    client: clientGraphql,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const { data: mostRecent } = useQuery(GET_PROVIDERS_MOST_RECENT, {
    client: clientGraphql,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const handleOnSelectedRowsChanged = useCallback(() => { }, []);
  const handleFilterChanged = useCallback(e => setKeyword(e.target.value), []);

  const transformProviders = useCallback(providers => {
    if (isEmpty(providers)) return [];

    return providers.flatMap(provider => {
      const docs = provider.documents || [];
      const requiredDocs = docs.filter(doc => doc.required);
      const optionalDocs = docs.filter(doc => !doc.required);

      return (provider.clinics || []).map(clinic => ({
        id: provider.id,
        display_name: provider.display_name,
        clinic,
        specializations: provider.specializations,
        documents: docs,
        requiredDocuments: requiredDocs,
        optionalDocuments: optionalDocs,
      }));
    });
  }, []);

  useEffect(() => {
    if (!data) return;

    const { data: providerData, total } = data.rows || { data: [], total: 0 };

    const filtered = (providerData || []).filter(i => i.can_receive);

    setParsedData({
      rows: transformProviders(filtered),
      count: total,
    });
  }, [data, transformProviders]);

  useEffect(() => {
    if (!isEmpty(favorites))
      setParsedFavorites({ rows: transformProviders(favorites.rows) });
  }, [favorites, transformProviders]);

  useEffect(() => {
    if (!isEmpty(mostRecent) && isEmpty(parsedMostRecent))
      setParsedMostRecent({ rows: transformProviders(mostRecent.rows) });
  }, [mostRecent, parsedMostRecent, transformProviders]);

  const showSideTables =
    !isEmpty(parsedMostRecent) || !isEmpty(parsedFavorites);

  return (
    <Grid container justifyContent="center" alignItems="flex-start">
      {showSideTables && (
        <Grid item md={3} xs={12} sx={{ px: 2, py: 2 }}>
          <Stack spacing={1}>
            <FindClinicFaveRecentTable
              label="Favorites"
              onClick={handleOnClickClinic}
              data={{
                rows: parsedFavorites.rows || [],
                count: parsedFavorites.rows?.length || 0,
              }}
            />
            <FindClinicFaveRecentTable
              label="Most Recent"
              onClick={handleOnClickClinic}
              data={{
                rows: parsedMostRecent.rows || [],
                count: parsedMostRecent.rows?.length || 0,
              }}
            />
          </Stack>
        </Grid>
      )}

      <Grid
        item
        md={showSideTables ? 9 : 12}
        xs={12}
        sx={{ px: 2, py: 2 }}
      >
        <FindClinicTable
          onFilterChanged={handleFilterChanged}
          onSelectedRowsChanged={handleOnSelectedRowsChanged}
          loading={loading}
          onClick={handleOnClickClinic}
          data={{
            rows: parsedData.rows || [],
            count: parsedData.count || 0,
          }}
          page={page}
          pageSize={pageSize}
          onPageChange={(newPage) => {
            setPage(newPage);
            refetch({ keyword, page: newPage, limit: pageSize });
          }}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(1);
            refetch({ keyword, page: 1, limit: newSize });
          }}
        />
      </Grid>
    </Grid>
  );
}
